import {useMemo, useState} from "react";
import {
    Button,
    Container,
    Heading,
    HStack,
    Input,
    Text,
    Textarea,
    VStack,
    useToast,
} from "@chakra-ui/react";
import {useNavigate} from "react-router-dom";
import {useCatalogItemStore} from "../store/catalogItem";

export default function CreatePage() {
    const [name, setName] = useState<string>("");
    const [price, setPrice] = useState<string>("");
    const [imageUrl, setImageUrl] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [description, setDescription] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const createCatalogItem = useCatalogItemStore((s) => s.createCatalogItem);

    const toast = useToast();
    const navigate = useNavigate();

    const hasUrl = imageUrl.trim().length > 0;
    const hasFile = imageFile !== null;

    const imageModeError = useMemo((): string | null => {
        if (hasUrl && hasFile) {
            return "Choose either URL or file, not both";
        }
        if (!hasUrl && !hasFile) {
            return "Provide image URL or upload a file";
        }
        return null;
    }, [hasUrl, hasFile]);

    const isCreateDisabled = useMemo((): boolean => {
        return imageModeError !== null || !name.trim();
    }, [name, imageModeError]);

    const clearFile = (): void => setImageFile(null);

    const handleFileChange = (file: File | null): void => {
        setImageFile(file);
        if (file) {
            setImageUrl("");
        }
    };

    const handleUrlChange = (value: string): void => {
        setImageUrl(value);
        if (value.trim()) {
            setImageFile(null);
        }
    };

    const handleCreate = async (): Promise<void> => {
        const nameValue = name.trim();
        if (!nameValue) {
            toast({title: "Name is required", status: "warning", isClosable: true});
            return;
        }

        if (imageModeError) {
            toast({title: imageModeError, status: "warning", isClosable: true});
            return;
        }

        const priceNumber = Number(price);

        const payload: {
            name: string;
            price: number;
            imageFile: File | null;
            description?: string;
            imageUrl?: string;
        } = {
            name: nameValue,
            price: Number.isFinite(priceNumber) ? priceNumber : 0,
            imageFile: imageFile,
        };

        const desc = description.trim();
        if (desc) {
            payload.description = desc;
        }

        const url = imageUrl.trim();
        if (url) {
            payload.imageUrl = url;
        }

        setIsSubmitting(true);
        try {
            const {success, message} = await createCatalogItem(payload);

            if (!success) {
                toast({
                    title: "Create failed",
                    description: message,
                    status: "error",
                    isClosable: true,
                });
                return;
            }

            toast({
                title: "Created",
                description: "Catalog item created",
                status: "success",
                isClosable: true,
            });

            setName("");
            setPrice("");
            setImageUrl("");
            setImageFile(null);
            setDescription("");

            navigate("/");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container maxW="container.sm" py={10}>
            <VStack spacing={4} align="stretch">
                <VStack spacing={1} align="start">
                    <Heading size="lg">Create Catalog Item</Heading>
                    <Text color="gray.500">Demo limit: up to 10 items per session</Text>
                </VStack>

                <Input
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                <Input
                    placeholder="Price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                />

                <VStack spacing={2} align="stretch">
                    <Text fontWeight="semibold">Image (URL or Upload)</Text>

                    <Input
                        placeholder="Image URL"
                        value={imageUrl}
                        onChange={(e) => handleUrlChange(e.target.value)}
                        isDisabled={isSubmitting || hasFile}
                    />

                    <HStack>
                        <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                            isDisabled={isSubmitting || hasUrl}
                            paddingTop={1.5}
                        />
                        <Button
                            variant="outline"
                            onClick={clearFile}
                            isDisabled={isSubmitting || !hasFile}
                        >
                            Remove file
                        </Button>
                    </HStack>

                    {hasFile && (
                        <Text fontSize="sm" color="gray.500">
                            Selected: {imageFile?.name} ({Math.round((imageFile?.size ?? 0) / 1024)} KB)
                        </Text>
                    )}

                    {imageModeError && (
                        <Text fontSize="sm" color="red.400">
                            {imageModeError}
                        </Text>
                    )}
                </VStack>

                <Textarea
                    placeholder="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <Button
                    colorScheme="blue"
                    onClick={handleCreate}
                    isLoading={isSubmitting}
                    isDisabled={isCreateDisabled || isSubmitting}
                >
                    Create
                </Button>
            </VStack>
        </Container>
    );
}